import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { MenuController } from '@ionic/angular';
import { Observable, Subject, take } from 'rxjs';
import { MS_PER_HOUR } from '@shared/core';
import { DEFAULT_AUTO_REMOVE_HOURS, Party, PartyTypes } from '@shared/party';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import {
  getReservationWaitlistHoursAhead,
  isInReservationWaitlistWindow,
} from '@tablesready/frontend-table';
import { ServerDate } from '@tablesready/server-time';
import { FloorPlanPartyType, getPartyType } from './get-party-type';

const AUTO_REMOVED_MESSAGE =
  'This party has been automatically removed according to the ' +
  '"Auto-remove waiting parties from your list after" setting';

export const PARTY_NOT_FOUND_MESSAGE = 'Party not found';

export const PARTY_HIGHLIGHT_QUERY_PARAM = 'partyId';
export const PARTY_HIGHLIGHT_ROW_ID_PREFIX = 'party-row-';
export const PARTY_HISTORY_HOURS_QUERY_PARAM = 'historyHours';

// Must match timeRangeOptions in history.component.ts, ascending order
const HISTORY_HOURS_OPTIONS = [1, 6, 12, 24, 48, 168];

const RESERVATIONS_ROUTE = '/reservations';

@Injectable({ providedIn: 'root' })
export class PartyNavigationService {
  // Emits the partyId every time the user clicks "View" — including same-URL
  // re-clicks where ActivatedRoute.queryParamMap would not emit. The page's
  // PartyHighlightService subscribes to this as a secondary trigger so the
  // highlight re-fires for the multiple-inbound-same-party scenario.
  private readonly _highlightRequested$ = new Subject<string>();
  readonly highlightRequested$: Observable<string> = this._highlightRequested$.asObservable();

  constructor(
    private readonly router: Router,
    private readonly menuController: MenuController,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly alertsService: AlertsService,
  ) {}

  navigateToPartyPage(party: Party): void {
    let route = this.resolveRoute(party);

    if (route === '/history') {
      if (this.isOutsideHistoryWindow(party)) {
        const requiredHours = this.getRequiredHistoryHours(party);
        if (requiredHours === null) {
          this.alertsService.addWarning(PARTY_NOT_FOUND_MESSAGE, 'Info:');
          return;
        }
        this.closeMenus();
        void this.router.navigate([route], {
          queryParams: {
            [PARTY_HIGHLIGHT_QUERY_PARAM]: party._id,
            [PARTY_HISTORY_HOURS_QUERY_PARAM]: requiredHours,
          },
        }).then(() => this._highlightRequested$.next(party._id));
        return;
      }
      this.closeMenus();
      const extras = this.navigationExtrasForRoute(party, route);
      void this.router.navigate([route], extras).then(() => {
        // Always fire the explicit highlight signal — covers the same-URL
        // re-click case where queryParamMap stays silent.
        this._highlightRequested$.next(party._id);
      });
      return;
    }

    this.authRestaurantService.selectWithAccountAndLocation()
      .pipe(take(1))
      .subscribe(({ location }) => {
        const autoRemoveHours = location.autoRemove || DEFAULT_AUTO_REMOVE_HOURS;
        const isAutoRemoved =
          party.quoted_time + autoRemoveHours * MS_PER_HOUR < ServerDate.now();

        if (isAutoRemoved) {
          this.alertsService.addWarning(AUTO_REMOVED_MESSAGE, 'Info:');
          return;
        }

        // From the waitlist, a reservation that the waitlist is currently showing
        // stays there, where the ready SMS can be sent.
        if (
          route === RESERVATIONS_ROUTE &&
          this.isOnWaitlistPage() &&
          isInReservationWaitlistWindow(
            party.quoted_time,
            getReservationWaitlistHoursAhead(location),
            ServerDate.now(),
          )
        ) {
          route = '/waitlist';
        }

        this.closeMenus();
        const extras = this.navigationExtrasForRoute(party, route);
        void this.router.navigate([route], extras).then(() => {
          // Always fire the explicit highlight signal — covers the same-URL
          // re-click case where queryParamMap stays silent.
          this._highlightRequested$.next(party._id);
        });
      });
  }

  // Always carry the partyId so the destination page can highlight the row.
  // Reservations additionally needs `date` (so the stepper lands on the right
  // day) and `queryParamsHandling: 'merge'` so it composes with any existing
  // query params instead of replacing them.
  //
  // `onSameUrlNavigation: 'reload'` is required for the multiple-inbound case:
  // after a highlight is consumed, PartyHighlightService.clearQueryParam uses
  // Location.replaceState to strip ?partyId, but the router's internal URL
  // state still carries it. A second "View" click on another inbound for the
  // same party therefore resolves to a URL the router already thinks is
  // current — without 'reload' that navigation is silently ignored and
  // queryParamMap never re-emits, so the highlight can't re-fire.
  private navigationExtrasForRoute(party: Party, route: string): NavigationExtras {
    if (route === RESERVATIONS_ROUTE) {
      return {
        queryParams: {
          date: party.quoted_time,
          [PARTY_HIGHLIGHT_QUERY_PARAM]: party._id,
        },
        queryParamsHandling: 'merge',
        onSameUrlNavigation: 'reload',
      };
    }
    return {
      queryParams: { [PARTY_HIGHLIGHT_QUERY_PARAM]: party._id },
      onSameUrlNavigation: 'reload',
    };
  }

  private resolveRoute(party: Party): string {
    const status = getPartyType(party);

    if (
      status === FloorPlanPartyType.canceled ||
      status === FloorPlanPartyType.serving ||
      status === FloorPlanPartyType.completed
    ) {
      return '/history';
    }

    switch (party.type) {
      case PartyTypes.walkIn:
        return '/waitlist';
      case PartyTypes.reservation:
        return RESERVATIONS_ROUTE;
      case PartyTypes.togo:
        return '/togo';
    }
  }

  private isOnWaitlistPage(): boolean {
    return this.router.url.split('?')[0] === '/waitlist';
  }

  private isOutsideHistoryWindow(party: Party): boolean {
    const historyWindowStart =
      ServerDate.now() - DEFAULT_AUTO_REMOVE_HOURS * MS_PER_HOUR;
    const visibleBySeated =
      party.seated_time != null && party.seated_time > historyWindowStart;
    const visibleByCanceled =
      party.canceled_time != null && party.canceled_time > historyWindowStart;
    return !visibleBySeated && !visibleByCanceled;
  }

  // Returns the smallest hours option that covers the party's age,
  // or null if the party is older than the maximum option (168h / 1 week).
  private getRequiredHistoryHours(party: Party): number | null {
    const relevantTime = party.seated_time ?? party.canceled_time;
    if (relevantTime == null) {
      return null;
    }
    const ageHours = (ServerDate.now() - relevantTime) / MS_PER_HOUR;
    return HISTORY_HOURS_OPTIONS.find(h => h > ageHours) ?? null;
  }

  private closeMenus(): void {
    void this.menuController.close('one-to-one-chat');
    void this.menuController.close('notification-center');
  }
}
